import {
  createContext,
  CSSProperties,
  FC,
  ReactNode,
  useContext,
  useLayoutEffect
} from "react";
import { PopoverOptions, usePopover } from "./usePopover";
import {
  FloatingPortal,
  FloatingFocusManager,
  FloatingArrow,
  useId,
  FloatingFocusManagerProps
} from "@floating-ui/react";
import { TEXT_STYLES, TextSizeType } from "../../styles";

export const PADDING_X = "px-3";
export const POPOVER_Z_INDEX = 50;

type PopoverContextType =
  | (ReturnType<typeof usePopover> & {
      setLabelId: (labelId: string) => void;
      setDescriptionId: (descriptionId: string) => void;
    })
  | null;
const PopoverContext = createContext<PopoverContextType>(null!);

export const usePopoverContext = () => {
  const context = useContext(PopoverContext);

  if (context == null) {
    throw new Error("Popover components must be wrapped in <Popover />");
  }

  return context;
};

export const Popover: FC<
  {
    children: ReactNode;
  } & PopoverOptions
> = ({ children, modal = false, ...options }) => {
  const popover = usePopover({ modal, ...options });
  return (
    <PopoverContext.Provider value={popover}>
      {children}
    </PopoverContext.Provider>
  );
};

export const PopoverTrigger: FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const context = usePopoverContext();
  const ref = context.refs.setReference;

  return (
    <div className={className} ref={ref} {...context.getReferenceProps()}>
      {children}
    </div>
  );
};

export const PopoverContent: FC<{
  children: ReactNode;
  heading?: string;
  description?: string;
  className?: string;
  initialFocus?: FloatingFocusManagerProps["initialFocus"];
  bgColor?: "white" | "black";
  style?: CSSProperties;
  closeOnClick?: boolean;
}> = ({
  children,
  className,
  heading,
  initialFocus = -1,
  bgColor = "white",
  style,
  closeOnClick
}) => {
  const {
    context: floatingContext,
    arrowRef,
    setOpen,
    ...context
  } = usePopoverContext();

  if (!floatingContext.open) return null;

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={floatingContext}
        modal={context.modal}
        initialFocus={initialFocus}
      >
        <div
          ref={context.refs.setFloating}
          style={{
            ...context.floatingStyles,
            ...style,
            zIndex: POPOVER_Z_INDEX
          }}
          className={`${
            bgColor === "white"
              ? "bg-white "
              : bgColor === "black"
              ? "bg-black border-black"
              : ""
          } rounded border shadow-md ${className}`}
          aria-labelledby={context.labelId}
          aria-describedby={context.descriptionId}
          {...context.getFloatingProps()}
          onClick={() => closeOnClick && setOpen(false)}
        >
          <FloatingArrow
            fill={
              bgColor === "white"
                ? "#ffffff"
                : bgColor === "black"
                ? "#000000"
                : ""
            }
            stroke={bgColor === "white" ? "#d1d5db" : "#000000"}
            strokeWidth={1}
            ref={arrowRef}
            context={floatingContext}
            tipRadius={2}
          />
          {heading && <PopoverHeading>{heading}</PopoverHeading>} {children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
};

export const PopoverHeading: FC<{
  headingSize?: TextSizeType;
  children: ReactNode;
}> = ({ headingSize = "body", children }) => {
  const { setLabelId } = usePopoverContext();
  const id = useId();

  // Only sets `aria-labelledby` on the Popover root element
  // if this component is mounted inside it.
  useLayoutEffect(() => {
    setLabelId(id);
    return () => setLabelId(undefined);
  }, [id, setLabelId]);

  return (
    <div className={`${PADDING_X} py-2 border-b`}>
      <h2 id={id} className={`${TEXT_STYLES[headingSize]} font-medium`}>
        {children}
      </h2>
    </div>
  );
};
